/**
 * Presentation mode: stage-only layout (body.presenting) + browser fullscreen.
 * If fullscreen is refused or unsupported, the layout still switches — handy
 * for capturing the window in OBS / TikTok LIVE Studio.
 *
 * `onChange(on)` runs synchronously on every switch, so on entry it is still
 * inside the user gesture (audio can be unlocked there), unless `beforeEnter`
 * awaited something first.
 *
 * `beforeEnter()` runs synchronously when entering is requested, so it is inside
 * the click too (screen capture needs that). It resolves to false to stay out.
 */
export function createPresentation({ onChange = () => {}, beforeEnter = () => true }) {
  let enteredFullscreen = false;

  const isOn = () => document.body.classList.contains('presenting');

  function sync() {
    onChange(isOn());
    window.dispatchEvent(new Event('resize')); // let canvases re-measure (and refit, below)
  }

  // Phones can bring their toolbars back (or keep the page scrolled) without 100dvh
  // catching up, which hides the bottom of the stage — Start! — with no way to scroll
  // to it. Fit the layout to what is actually visible instead.
  function fitViewport() {
    if (!isOn()) return;
    const vv = window.visualViewport;
    const height = vv ? vv.height * vv.scale : window.innerHeight;
    document.body.style.setProperty('--present-height', `${Math.floor(height)}px`);
    if (window.scrollX || window.scrollY) window.scrollTo(0, 0);
  }
  window.visualViewport?.addEventListener('resize', fitViewport);
  window.addEventListener('resize', fitViewport);

  async function enter() {
    if (isOn()) return;
    if (!(await beforeEnter())) return;
    document.body.classList.add('presenting');
    fitViewport();
    sync();
    if (document.fullscreenEnabled && !document.fullscreenElement) {
      try {
        await document.documentElement.requestFullscreen({ navigationUI: 'hide' });
        enteredFullscreen = true;
      } catch {
        enteredFullscreen = false;
      }
    }
  }

  async function exit() {
    document.body.classList.remove('presenting');
    document.body.style.removeProperty('--present-height');
    sync();
    if (document.fullscreenElement && enteredFullscreen) {
      enteredFullscreen = false;
      await document.exitFullscreen().catch(() => {});
    }
  }

  document.addEventListener('fullscreenchange', () => {
    // User left fullscreen with Esc / browser UI → leave presentation too.
    if (!document.fullscreenElement && enteredFullscreen) {
      enteredFullscreen = false;
      if (isOn()) {
        document.body.classList.remove('presenting');
        document.body.style.removeProperty('--present-height');
        sync();
      }
    }
  });

  return { enter, exit, toggle: () => (isOn() ? exit() : enter()), isOn };
}
